import type { DomainError } from '@core/domain';

import { adaptArrayGraphQLError, isArrayGraphQLError } from './adaptArrayGraphQLError';
import { adaptGenericError } from './genericErrorAdapter';
import { isGraphQLError, adaptGraphQLError } from './graphqlErrorAdapter';
import { isDomainError } from './isDomainError';
import { isNetworkError, adaptNetworkError } from './networkErrorAdapter';

/**
 * Transforms any error into a domain error
 * For backward compatibility, this always returns a single error
 * Use transformErrors() if you need to handle multiple errors
 */
export function transformError(error: unknown): DomainError {
  // Already a domain error
  if (isDomainError(error)) {
    return error;
  }

  // Array-style GraphQL errors
  if (isArrayGraphQLError(error)) {
    // For backward compatibility, return the first error from the array
    const errors = adaptArrayGraphQLError(error);

    return errors[0];
  }

  // Object-style GraphQL errors
  if (isGraphQLError(error)) {
    // For backward compatibility, always return the first error
    return adaptGraphQLError(error);
  }

  // Network error
  if (isNetworkError(error)) {
    return adaptNetworkError(error);
  }

  // Generic error
  return adaptGenericError(error);
}

/**
 * Transforms any error into an array of domain errors
 * This is useful when you want to handle multiple errors at once
 * For ApolloErrors with multiple GraphQL errors, this will return all errors
 */
export function transformErrors(error: unknown): DomainError[] {
  // Already a domain error
  if (isDomainError(error)) {
    return [error];
  }

  // Array-style GraphQL errors
  if (isArrayGraphQLError(error)) {
    return adaptArrayGraphQLError(error);
  }

  // Object-style GraphQL errors (ApolloError)
  if (isGraphQLError(error)) {
    const graphQLErrorObj = error as { graphQLErrors?: unknown[] };

    // Verify that graphQLErrors exists and is an array
    if (Array.isArray(graphQLErrorObj.graphQLErrors) && graphQLErrorObj.graphQLErrors.length > 1) {
      return graphQLErrorObj.graphQLErrors.map(err =>
        // Handle each GraphQL error individually
        adaptGraphQLError({ graphQLErrors: [err] })
      );
    }

    // Otherwise, return a single domain error in an array
    return [adaptGraphQLError(error)];
  }

  // Network error
  if (isNetworkError(error)) {
    return [adaptNetworkError(error)];
  }

  // Generic error
  return [adaptGenericError(error)];
}

/**
 * Extracts user-friendly error messages from an array of domain errors
 * This is useful for displaying error messages to the user in UI components
 * @param errors Array of domain errors
 * @returns Array of user-friendly error messages
 */
export function extractUserMessages(errors: DomainError[]): string[] {
  if (!errors || errors.length === 0) {
    return [];
  }

  return errors.map(error => error.userMessage);
}

/**
 * Formats error messages as an array of strings
 * This is useful for displaying error messages in UI components
 * @param errors Array of domain errors
 * @returns Array of user-friendly error messages (never empty, but may be an empty array)
 */
export function formatErrorMessages(errors: DomainError[]): string[] {
  if (!errors || errors.length === 0) {
    return [];
  }

  return extractUserMessages(errors).filter(Boolean);
}
